import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { IMAGES } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border-0 border-b border-secondary-foreground/30 focus:border-primary focus:ring-0 outline-none px-0 py-4 font-mono text-sm uppercase tracking-widest placeholder:text-secondary-foreground/40 transition-colors duration-300";

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, phone: form.phone || null });
      toast.success("Enquiry sent — the seller will be in touch shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Could not send your enquiry. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="bg-secondary text-secondary-foreground border-t border-secondary-foreground/20 py-24 md:py-40" data-testid="contact-section">
      <div className="container mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-12 md:gap-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-5"
          >
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-4">Final chapter — yours to write</p>
            <h2 className="font-headings uppercase text-5xl md:text-7xl leading-[0.85] mb-8" data-testid="contact-heading">
              Make it<br /><span className="text-primary">Yours</span>
            </h2>
            <p className="font-editorial italic text-xl md:text-2xl text-accent leading-snug mb-8">
              “Thirty-eight years old and still the fastest way to slow down.”
            </p>
            <img
              src={IMAGES.owner}
              alt="The van's owner"
              className="w-44 h-44 md:w-60 md:h-60 object-cover border-2 border-accent -rotate-3 shadow-lg mb-8 ml-4 md:ml-16"
              data-testid="owner-photo"
            />
            <div className="space-y-3 font-mono text-xs uppercase tracking-[0.25em] text-secondary-foreground/60">
              <p>Price — $25,000</p>
              <p>Mileage — 224,000 miles</p>
              <p>Location — Marin County, CA</p>
              <p>Viewings — by appointment</p>
              <p>Serious enquiries — answered personally</p>
            </div>
          </motion.div>

          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-7 flex flex-col gap-8"
            data-testid="enquiry-form"
          >
            <div className="grid sm:grid-cols-2 gap-8">
              <div>
                <label htmlFor="enquiry-name" className="font-mono text-[10px] uppercase tracking-[0.3em] text-secondary-foreground/50">Your name</label>
                <input id="enquiry-name" data-testid="enquiry-name-input" required value={form.name} onChange={set("name")} placeholder="J. OVERLAND" className={inputCls} maxLength={100} />
              </div>
              <div>
                <label htmlFor="enquiry-email" className="font-mono text-[10px] uppercase tracking-[0.3em] text-secondary-foreground/50">Email</label>
                <input id="enquiry-email" data-testid="enquiry-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="YOU@SOMEWHERE.COM" className={inputCls} />
              </div>
            </div>
            <div>
              <label htmlFor="enquiry-phone" className="font-mono text-[10px] uppercase tracking-[0.3em] text-secondary-foreground/50">Phone (optional)</label>
              <input id="enquiry-phone" data-testid="enquiry-phone-input" value={form.phone} onChange={set("phone")} placeholder="+1 ..." className={inputCls} maxLength={40} />
            </div>
            <div>
              <label htmlFor="enquiry-message" className="font-mono text-[10px] uppercase tracking-[0.3em] text-secondary-foreground/50">Message</label>
              <textarea id="enquiry-message" data-testid="enquiry-message-input" required rows={4} value={form.message} onChange={set("message")} placeholder="TELL US ABOUT YOUR PLANS FOR THE VAN..." className={`${inputCls} resize-none normal-case tracking-normal`} maxLength={3000} />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="enquiry-submit-button"
              className="group relative self-start overflow-hidden border border-primary px-10 py-5 font-mono text-xs uppercase tracking-[0.3em] text-primary-foreground disabled:opacity-60"
            >
              <span className="absolute inset-0 bg-primary transition-transform duration-500 ease-out group-hover:translate-x-full" />
              <span className="absolute inset-0 bg-primary -translate-x-full transition-transform duration-500 ease-out group-hover:translate-x-0" />
              <span className="relative z-10 flex items-center gap-3">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />}
                {sending ? "Sending" : "Send enquiry"}
              </span>
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
